import { Injectable } from '@angular/core';
import { ApiDonorResponse } from '../../modules/donation-portal/features/donors/models/donor-management.response';
import { DonationResponse } from '../../modules/donation-portal/features/donations/models/donations.response';
import { ApiContainerResponse } from '../../modules/donation-portal/features/containers/models/containers.response';

interface ExportedData {
  donors?: ApiDonorResponse[];
  donations?: DonationResponse[];
  containers?: ApiContainerResponse[];
  version?: string;
  exportedAt?: string;
}

/**
 * Service for persisting donation app data to localStorage
 * Provides methods to save and restore state across sessions
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly STORAGE_KEYS = {
    donors: 'donation-app-donors',
    donations: 'donation-app-donations',
    containers: 'donation-app-containers',
    version: 'donation-app-version'
  };

  private readonly CURRENT_VERSION = '1.0.0';

  constructor() {
    this.checkVersion();
  }

  // ─── Donors ───

  saveDonors(donors: ApiDonorResponse[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.donors, JSON.stringify(donors));
    } catch (error) {
      console.error('Failed to save donors to localStorage:', error);
    }
  }

  loadDonors(): ApiDonorResponse[] | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.donors);
      if (!data) return null;
      return JSON.parse(data) as ApiDonorResponse[];
    } catch (error) {
      console.error('Failed to load donors from localStorage:', error);
      return null;
    }
  }

  // ─── Donations ───

  saveDonations(donations: DonationResponse[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.donations, JSON.stringify(donations));
    } catch (error) {
      console.error('Failed to save donations to localStorage:', error);
    }
  }

  loadDonations(): DonationResponse[] | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.donations);
      if (!data) return null;
      return JSON.parse(data) as DonationResponse[];
    } catch (error) {
      console.error('Failed to load donations from localStorage:', error);
      return null;
    }
  }

  // ─── Containers ───

  saveContainers(containers: ApiContainerResponse[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.containers, JSON.stringify(containers));
    } catch (error) {
      console.error('Failed to save containers to localStorage:', error);
    }
  }

  loadContainers(): ApiContainerResponse[] | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.containers);
      if (!data) return null;
      return JSON.parse(data) as ApiContainerResponse[];
    } catch (error) {
      console.error('Failed to load containers from localStorage:', error);
      return null;
    }
  }

  // ─── Version Management ───

  private checkVersion(): void {
    const storedVersion = localStorage.getItem(this.STORAGE_KEYS.version);
    if (storedVersion !== this.CURRENT_VERSION) {
      // Version mismatch - clear old data
      console.log('Version mismatch detected. Clearing old data.');
      this.clearAll();
      localStorage.setItem(this.STORAGE_KEYS.version, this.CURRENT_VERSION);
    }
  }

  // ─── Utility Methods ───

  clearAll(): void {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.setItem(this.STORAGE_KEYS.version, this.CURRENT_VERSION);
  }

  hasStoredData(): boolean {
    return (
      !!localStorage.getItem(this.STORAGE_KEYS.donors) ||
      !!localStorage.getItem(this.STORAGE_KEYS.donations) ||
      !!localStorage.getItem(this.STORAGE_KEYS.containers)
    );
  }

  exportData(): string {
    return JSON.stringify(
      {
        donors: this.loadDonors(),
        donations: this.loadDonations(),
        containers: this.loadContainers(),
        version: this.CURRENT_VERSION,
        exportedAt: new Date().toISOString()
      },
      null,
      2
    );
  }

  importData(jsonData: string): boolean {
    try {
      const data: ExportedData = JSON.parse(jsonData);
      if (data.donors) this.saveDonors(data.donors);
      if (data.donations) this.saveDonations(data.donations);
      if (data.containers) this.saveContainers(data.containers);
      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }
}
